import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from "@chakra-ui/react";
import ReactJson, { InteractionProps } from "react-json-view";
import { useEffect, useState } from "react";

import { PluginType } from "../../../plugins/ai";
import { useAppDispatch, useAppSelector } from "../../../store";
import { updateLessonState } from "../../../store/slices/lessonSlice";
import CodeEditor from "./CodeMirror";

type IProps = {
  isOpen: boolean;
  currentContentId: number | null;
  onClose: () => void;
  onClick: (plugin: PluginType) => void;
};

const EditPluginPropsChange = ({
  isOpen,
  onClose,
  currentContentId,
}: IProps) => {
  const { currentLesson } = useAppSelector((s) => s.lesson);
  const dispatch = useAppDispatch();

  const [props, setProps] = useState<Record<string, any>>({});
  const [raw, setRaw] = useState("");

  useEffect(() => {
    if (currentContentId === null || !currentLesson.content[currentContentId])
      return;
    const content = currentLesson.content[currentContentId];
    const initialProps = content.props || {};
    setProps(initialProps);
    setRaw(JSON.stringify(initialProps, null, 2));
  }, [currentContentId, isOpen, currentLesson.content]);

  const handleJsonChange = (edit: InteractionProps) => {
    if (edit.updated_src) {
      setProps(edit.updated_src);
      setRaw(JSON.stringify(edit.updated_src, null, 2));
    }
  };

  const handleRawChange = (rawString: string) => {
    setRaw(rawString);
    try {
      const parsed = JSON.parse(rawString);
      setProps(parsed);
    } catch (err) {
      // silently fail while typing
    }
  };

  const handleSave = () => {
    if (currentContentId === null) return;

    const updatedContent = [...currentLesson.content];
    const originalComponent = currentLesson.content[currentContentId];

    updatedContent[currentContentId] = {
      ...originalComponent,
      props,
    } as any;

    dispatch(
      updateLessonState({
        currentLesson: {
          ...currentLesson,
          content: updatedContent,
        },
      })
    );

    onClose();
    setProps({});
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => {
        onClose();
        setProps({});
      }}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader bg="neutral.950">Edit Plugin Props</DrawerHeader>

        <DrawerBody bg="neutral.950" py={4}>
          <VStack spacing={6} align="stretch">
            <Tabs variant="enclosed" colorScheme="teal">
              <TabList>
                <Tab>JSON View</Tab>
                <Tab>Raw Editor</Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <ReactJson
                    src={props}
                    theme="monokai"
                    name={false}
                    onEdit={handleJsonChange}
                    onAdd={handleJsonChange}
                    onDelete={handleJsonChange}
                    displayDataTypes={false}
                  />
                </TabPanel>

                <TabPanel px={0}>
                  <CodeEditor content={raw} onChange={handleRawChange} />
                </TabPanel>
              </TabPanels>
            </Tabs>

            <Button variant={"solid"} w={"fit-content"} onClick={handleSave}>
              Save
            </Button>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default EditPluginPropsChange;
